import {LinkText} from '@/components/atoms';
import PeacockDataTable from '@/components/organisms/PeacockDataTable';
import {getCellDataMappedCols} from '@/helpers/dataTableCellDataMapper';
import {useBulkModifiers} from '@/hooks/useBulkModifiers';
import {usePeacockDataTableConfig} from '@/hooks/usePeacockDataTableConfig';
import {useAppDispatch} from '@/store';
import {resetBulkModifiers} from '@/store/slices/bulkPointSlice';
import {formatDateToLocalYYYYMMDD} from '@/utility/dateHelper';
import {ROUTES} from '@/utils/constants/routesConstants';
import {STATUS_VALUES} from '@/utils/constants/commonConstants';
import {useCallback, useMemo} from 'react';
import {useNavigate} from 'react-router-dom';

const navigateToBulkModifier = (navigate, row) => {
  if (row?.status === STATUS_VALUES.Draft || !row?.id) {
    navigate(`${ROUTES.BULK_POINT_MODIFIER_VIEW.replace(':id', row?.uuid)}`, {
      state: {
        uuid: row?.uuid,
        formData: row
      }
    });
  } else {
    navigate(`${ROUTES.BULK_POINT_MODIFIER_VIEW.replace(':id', row?.id)}`, {
      state: {id: row.id}
    });
  }
};

const cellDataMapperObj = (navigate) => ({
  bulkModifierName: {
    cellData: ({row: {original} = {}}) => (
      <LinkText
        text={original?.bulkModifierName || '\u2014'}
        id={original?.bulkModifierName + ' ' + (original?.id || original?.uuid)}
        handleClick={() => navigateToBulkModifier(navigate, original)}
      />
    ),
    linkHandler: (row) => navigateToBulkModifier(navigate, row),
    linkLabel: (row) => row.bulkModifierName || '\u2014'
  },
  noOfStores: {
    cellData: ({row: {original} = {}}) => (
      <div>{original?.noOfStores ?? '\u2014'}</div>
    )
  },
  createdAt: {
    cellData: ({row: {original} = {}}) => (
      <div>{formatDateToLocalYYYYMMDD(original?.createdAt) || '\u2014'}</div>
    )
  },
  scheduledAt: {
    cellData: ({row: {original} = {}}) => (
      <div>{formatDateToLocalYYYYMMDD(original?.scheduledAt) || '\u2014'}</div>
    )
  },
  status: {
    cellData: ({row: {original} = {}}) => (
      <div className={original?.status === 'FAILED' ? 'status_error' : ''}>
        {original?.status ?? '\u2014'}
      </div>
    )
  }
});

const BulkModifiersList = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    bulkModifiersData,
    totalRecords,
    bulkModifiersColConfigData,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    setSortingObj,
    setSearchArry,
    isLoading,
    isFetching,
    sortingObj,
    handleResetAll,
    onDownloadBtnClick
  } = useBulkModifiers();

  const columns = useMemo(
    () =>
      getCellDataMappedCols(
        cellDataMapperObj(navigate),
        bulkModifiersColConfigData
      ),
    [navigate, bulkModifiersColConfigData]
  );

  const {
    records,
    handleDownload,
    paginationConfig,
    sortingConfig,
    filterConfig,
    selectionConfig
  } = usePeacockDataTableConfig({
    data: bulkModifiersData,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    setSortingObj,
    sortingObj,
    setSearchArry,
    handleResetAll,
    onDownloadBtnClick
  });

  const handleResetFormData = useCallback(() => {
    dispatch(resetBulkModifiers());
    navigate(ROUTES.BULK_POINT_MODIFIER_CREATE);
  }, [dispatch, navigate]);

  const toolbarConfig = useMemo(
    () => ({
      showDownload: true,
      onDownload: handleDownload,
      showCreateNew: true,
      onCreateNew: handleResetFormData,
      alwaysShowInToolbar: true
    }),
    [handleDownload, handleResetFormData]
  );

  return (
    <div className='bulkModifiersDataTable w-full'>
      <PeacockDataTable
        columns={columns}
        records={records}
        isLoading={isLoading}
        isFetching={isFetching}
        pageSize={pageSize}
        totalRecords={totalRecords}
        paginationConfig={paginationConfig}
        selectionConfig={selectionConfig}
        sortingConfig={sortingConfig}
        filterConfig={filterConfig}
        toolbarConfig={toolbarConfig}
      />
      {totalRecords > 0 && (
        <div className='text-md py-2 pr-4 text-right text-[color:var(--color-text-primary)]'>
          {totalRecords} total items
        </div>
      )}
    </div>
  );
};

export default BulkModifiersList;
